// © 2024–2026 Jos Jozef Sytze van der Werf — Alle rechten voorbehouden.
import { ShieldCheck } from 'lucide-react'
import { InfoBlock }   from '@/components/ui/InfoBlock'
import { YesNoStep }   from './YesNoStep'
import { Input }       from '@/components/ui/Input'
import { Card }        from '@/components/ui/Card'
import { useWizardStore, useAuthStore } from '@/store'
import {
  loadLivingWillDetails, saveLivingWillDetails,
  EMPTY_LIVING_WILL_DETAILS, type LivingWillDetails,
  loadTestamentDetails,
} from '@/services/wizard'
import { saveDocument } from '@/services/wishes'
import { useState, useEffect, useCallback } from 'react'
import { useAutosave } from '@/hooks/useAutosave'
import type { SaveStatus } from '@/hooks/useAutosave'

export function NewStep10LivingWill({ onSaveStatus }: { onSaveStatus: (s: SaveStatus) => void }) {
  const { answers, setAnswer } = useWizardStore()
  const { userId }             = useAuthStore()
  const [details,   setDetails]   = useState<LivingWillDetails>(EMPTY_LIVING_WILL_DETAILS)
  const [ready,     setReady]     = useState(false)
  const [notarisPrefill, setNotarisPrefill] = useState('')
  const [cityPrefill,    setCityPrefill]    = useState('')
  const hasLivingWill = answers.hasLivingWill

  useEffect(() => { onSaveStatus('idle') }, [onSaveStatus])

  useEffect(() => {
    if (!userId) return
    loadLivingWillDetails(userId).then(d => {
      if (d) setDetails(d)
      setReady(true)
    })
    // Punt I - prefill notarisgegevens vanuit testament indien ingevuld
    loadTestamentDetails(userId).then(t => {
      if (t?.notarisName) setNotarisPrefill(t.notarisName)
      if (t?.notarisCity)  setCityPrefill(t.notarisCity)
    })
  }, [userId])

  const save = useCallback(async (d: LivingWillDetails) => {
    if (!userId) return
    await saveLivingWillDetails(userId, d)
    // Documentkluis koppeling: als documentLocation is ingevuld
    if (d.documentLocation.trim()) {
      await saveDocument(userId, {
        category:   'LEGAL',
        label:      'Levenstestament',
        location:   d.documentLocation,
        notes:      d.notarisName ? `Notaris: ${d.notarisName}${d.notarisCity ? ', ' + d.notarisCity : ''}` : '',
        isVerified: false,
      })
    }
  }, [userId])

  const { status } = useAutosave({ data: details, onSave: save, enabled: ready && hasLivingWill === true })
  useEffect(() => { onSaveStatus(status) }, [status, onSaveStatus])

  function handleChange(v: string) {
    setAnswer('hasLivingWill', v === 'yes')
    onSaveStatus('saved')
    setTimeout(() => onSaveStatus('idle'), 1500)
  }

  const value = hasLivingWill === true ? 'yes' : hasLivingWill === false ? 'no' : ''

  // Prefill-suggestie: als testament-notaris bekend maar LT-notaris leeg
  const showPrefill = !details.notarisName && notarisPrefill

  return (
    <div className="flex flex-col gap-6">

      {/* Goed om te weten VOOR de vraag (punt 21) */}
      <InfoBlock title="Goed om te weten - levenstestament" variant="blue" defaultOpen={false}>
        <p>Een levenstestament gaat niet over later, maar over nu: wie regelt uw zaken als u dat zelf even niet meer kunt, bijvoorbeeld na een beroerte of bij dementie. Een testament werkt ná uw overlijden, een levenstestament tijdens uw leven.</p>
        <p>Vaak legt u twee dingen vast: een <strong>financiële volmacht</strong>, waarmee iemand uw bankzaken regelt en contracten tekent, en een <strong>medische volmacht</strong>, waarmee iemand namens u medische beslissingen neemt. Meestal is dat uw partner, een kind of een vertrouwde vriend.</p>
        <p>Zonder levenstestament moet de familie naar de kantonrechter voor een bewindvoerder of mentor. Dat duurt, kost geld, en u heeft dan geen invloed meer op wie het wordt.</p>
                                        <p className="mt-2">
          <a href="https://www.knb.nl/kennisbank/familie-erfrecht/levenstestament/" target="_blank" rel="noopener noreferrer"
             className="text-xs text-sage-600 underline underline-offset-2 hover:text-sage-800">
            Notaris.nl - meer over het levenstestament en hoe u er een laat opstellen →
          </a>
        </p>
        <p className="text-xs text-ink-4 mt-2 pt-2 border-t border-sand-200 leading-relaxed">
          WilsApp legt uw wensen vast als persoonlijke leidraad. Voor officiële registratie,
          juridische geldigheid of medische besluitvorming verwijzen wij naar de bevoegde instantie.
        </p>
      </InfoBlock>

      <YesNoStep
        title="Levenstestament"
        question="Heeft u een levenstestament?"
        description="Een levenstestament regelt wie uw zaken behartigt als u dat zelf niet meer kunt."
        value={value}
        onChange={handleChange}
        conditionKey="yes"
        conditionContent={
          <div className="flex flex-col gap-4">
            <div className="flex items-start gap-3 bg-white rounded-2xl border border-sage-200 p-4">
              <ShieldCheck className="h-5 w-5 text-sage-600 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-semibold text-ink mb-1">Notarisgegevens</p>
                <p className="text-xs text-ink-3">Noteer bij welke notaris uw levenstestament ligt.</p>
              </div>
            </div>

            {/* Punt I: prefill vanuit testament-notaris */}
            {showPrefill && (
              <div className="flex items-start gap-3 rounded-xl bg-sage-50 border border-sage-200 px-4 py-3">
                <span className="text-sm flex-shrink-0">💡</span>
                <div className="flex-1">
                  <p className="text-xs text-sage-700 mb-2">
                    Bij uw testament staat notaris <strong>{notarisPrefill}</strong> vermeld. Zelfde notaris?
                  </p>
                  <button
                    type="button"
                    onClick={() => setDetails(p => ({ ...p, notarisName: notarisPrefill, notarisCity: cityPrefill }))}
                    className="text-xs font-semibold text-sage-700 underline underline-offset-2 hover:text-sage-900"
                  >
                    Ja, gebruik dezelfde notaris
                  </button>
                </div>
              </div>
            )}

            <Card>
              <div className="flex flex-col gap-3">
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input label="Naam notaris" value={details.notarisName}
                    onChange={e => setDetails(p => ({ ...p, notarisName: e.target.value }))}
                    placeholder="Notariskantoor De Vries" />
                  <Input label="Plaats notaris" value={details.notarisCity}
                    onChange={e => setDetails(p => ({ ...p, notarisCity: e.target.value }))}
                    placeholder="Amsterdam" />
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  <Input label="Jaar van opstellen" value={details.year}
                    onChange={e => setDetails(p => ({ ...p, year: e.target.value }))}
                    placeholder="Bijv: 2020" />
                  <Input label="Locatie document" value={details.documentLocation}
                    onChange={e => setDetails(p => ({ ...p, documentLocation: e.target.value }))}
                    placeholder="Bijv: bij notaris, kluis thuis" />
                </div>
                {details.documentLocation && (
                  <p className="text-xs text-sage-600">
                    ✓ Locatie wordt ook opgeslagen in uw documentwijzer.
                  </p>
                )}
              </div>
            </Card>
          </div>
        }
      />

      <InfoBlock title="Uitleg: levenstestament" defaultOpen={false}>
        <p><strong>Financiële volmacht:</strong> iemand regelt bankzaken, contracten en belastingaangifte namens u.</p>
        <p><strong>Medische volmacht:</strong> iemand neemt medische beslissingen als u dat niet meer kunt.</p>
        <p><strong>Zorgvolmacht:</strong> iemand regelt uw woon- en zorgsituatie.</p>
        <p><strong>Registratie:</strong> noteer het levenstestament in het Centraal Levenstestamentenregister (CLTR).</p>
        <p><strong>Verbinding met medische wensen:</strong> medische wensen, een euthanasieverzoek of behandelverbod worden
        vaak opgenomen in het levenstestament of in een aparte medische wilsverklaring. Overleg met uw notaris en huisarts
        welke vorm het beste bij uw situatie past.</p>
        <p className="mt-1">
          <a href="https://www.knb.nl/kennisbank/familie-erfrecht/levenstestament/" target="_blank" rel="noopener noreferrer"
             className="text-xs text-sage-600 underline underline-offset-2 hover:text-sage-800">
            KNB - informatie over het levenstestament →
          </a>
        </p>
      </InfoBlock>

      {/* V41: Volmachthouder levenstestament */}
      {(details.year || details.notarisName) && (
        <div className="flex flex-col gap-3 rounded-2xl border border-sand-300 bg-white px-5 py-4">
          <p className="text-sm font-semibold text-ink">Volmachthouder (optioneel)</p>
          <p className="text-xs text-ink-4">Wie mag namens u beslissingen nemen als u dat zelf niet meer kunt?</p>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input label="Naam volmachthouder" value={details.proxyName}
              onChange={e => setDetails(p => ({ ...p, proxyName: e.target.value }))}
              placeholder="Voornaam Achternaam" />
            <Input label="Relatie" value={details.proxyRelation}
              onChange={e => setDetails(p => ({ ...p, proxyRelation: e.target.value }))}
              placeholder="Bijv: partner, dochter" />
          </div>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input label="Telefoonnummer" type="tel" value={details.proxyPhone}
              onChange={e => setDetails(p => ({ ...p, proxyPhone: e.target.value }))} placeholder="06-..." />
            <Input label="E-mailadres" type="email" value={details.proxyEmail}
              onChange={e => setDetails(p => ({ ...p, proxyEmail: e.target.value }))} placeholder="naam@mail.nl" />
          </div>
          <div>
            <p className="text-xs font-semibold text-ink-4 uppercase tracking-wider mb-2">Type volmacht</p>
            <div className="flex flex-wrap gap-2">
              {([
                { value: 'financial', label: 'Financieel' },
                { value: 'medical',   label: 'Medisch' },
                { value: 'both',      label: 'Financieel én medisch' },
              ] as const).map(opt => (
                <button key={opt.value} type="button"
                  onClick={() => setDetails(p => ({ ...p, proxyScope: opt.value }))}
                  className={`rounded-xl border-2 px-3 py-1.5 text-xs font-medium transition-all ${
                    details.proxyScope === opt.value
                      ? 'border-sage-600 bg-sage-50 text-sage-800'
                      : 'border-sand-300 text-ink-3 hover:border-sage-300'
                  }`}>{opt.label}</button>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* V38: Koppeling medische wensen ↔ levenstestament */}
      <div className="rounded-2xl bg-pink-50 border border-pink-200 px-5 py-4 flex flex-col gap-2">
        <p className="text-sm font-semibold text-pink-900">Samenhang met medische wensen</p>
        <p className="text-xs text-pink-700 leading-relaxed">
          Heeft u medische wensen, een euthanasieverzoek of een behandelverbod vastgelegd?
          Neem deze ook op bij <strong>Medische wensen</strong> in WilsApp. Zo houdt u zelf het
          overzicht en weten uw naasten later precies waar zij belangrijke informatie kunnen vinden.
        </p>
        <p className="text-xs text-pink-600 leading-relaxed">
          WilsApp begeleidt u stap voor stap bij het vastleggen van uw wensen. Voor de juridische
          vastlegging en medische uitvoering verwijzen wij naar uw notaris en huisarts.
        </p>
      </div>

    </div>
  )
}
